import { useEffect, useState } from "react";
import axios from "axios";
import { Link, useNavigate, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Loader2, Play, FileText, Gauge, Lock, ArrowRight, ArrowLeft, Share2 } from "lucide-react";
import { Header } from "../components/Header";
import { Footer } from "../components/Footer";
import { WhatsAppFloat } from "../components/WhatsAppFloat";
import { useSite, waLink, resolveMedia, getCustomerToken, custHeaders } from "../siteContext";
import { useLang, pick } from "../i18n";
import { effPrice, hasSpecial } from "../components/Courses";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function CoursePage() {
  const { id } = useParams();
  const settings = useSite();
  const { lang, s } = useLang();
  const navigate = useNavigate();
  const [courses, setCourses] = useState(null);
  const ar = lang === "ar";
  const Arrow = ar ? ArrowRight : ArrowLeft;

  useEffect(() => {
    window.scrollTo(0, 0);
    axios.get(`${API}/courses`).then((res) => setCourses(Array.isArray(res.data) ? res.data : [])).catch(() => setCourses([]));
  }, []);

  const course = courses ? courses.find((c) => c.id === id) : undefined;

  const buy = async () => {
    if (course.payment_link) {
      window.location.href = course.payment_link;
      return;
    }
    if (effPrice(course) > 0) {
      if (!getCustomerToken()) {
        navigate(`/account?buy=${course.id}`);
        return;
      }
      try {
        window.location.href = `/course/${course.id}`;
      } catch (err) {
        toast.error(ar ? "تعذر فتح صفحة الدورة" : "Could not open the course page");
      }
      return;
    }
    window.open(waLink(settings, s.enrollMsg(pick(lang, course, "title")), lang), "_blank", "noopener,noreferrer");
  };

  const share = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      toast.success(ar ? "تم نسخ رابط الدورة" : "Course link copied");
    } catch {
      toast.error(ar ? "تعذر النسخ" : "Copy failed");
    }
  };

  return (
    <div className="min-h-screen bg-luxury-black">
      <Header />
      <main className="pt-32 pb-24">
        <div className="max-w-5xl mx-auto px-4 md:px-8">
          {courses === null ? (
            <div className="flex justify-center py-24"><Loader2 className="w-8 h-8 text-luxury-gold animate-spin" /></div>
          ) : !course ? (
            <div data-testid="course-not-found" className="text-center py-24 border border-luxury-border bg-luxury-surface rounded-2xl">
              <p className="text-luxury-gray mb-6">{ar ? "هذه الدورة غير موجودة" : "This course does not exist"}</p>
              <Link to="/" className="text-luxury-gold hover:text-luxury-goldLight transition-colors">{ar ? "العودة للرئيسية" : "Back home"}</Link>
            </div>
          ) : (
            <motion.div data-testid="course-page" initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}>
              <div className="flex items-center justify-between mb-10">
                <Link to="/#courses" data-testid="course-back-link" className="inline-flex items-center gap-2 text-sm text-luxury-gray hover:text-luxury-gold transition-colors">
                  <Arrow className="w-4 h-4" strokeWidth={1.5} />
                  {ar ? "عودة للدورات" : "Back to courses"}
                </Link>
                <button data-testid="course-share-button" onClick={share} className="inline-flex items-center gap-2 text-sm text-luxury-gray hover:text-luxury-gold transition-colors">
                  <Share2 className="w-4 h-4" strokeWidth={1.5} />
                  {ar ? "مشاركة الرابط" : "Share link"}
                </button>
              </div>

              {course.teaser ? (
                <div className="relative rounded-3xl overflow-hidden border border-luxury-border mb-10">
                  <p className="absolute top-4 start-4 z-10 text-[11px] tracking-[0.2em] bg-luxury-black/70 border border-luxury-border text-luxury-gold rounded-full px-3 py-1.5">
                    {s.course_teaser}
                  </p>
                  <video
                    data-testid="course-page-teaser"
                    controls
                    muted
                    playsInline
                    controlsList="nodownload"
                    onContextMenu={(e) => e.preventDefault()}
                    className="w-full aspect-video bg-black"
                    src={`${API}/videos/${course.teaser.id}/stream`}
                  />
                </div>
              ) : course.image ? (
                <div className="rounded-3xl overflow-hidden border border-luxury-border mb-10 h-[320px] md:h-[420px]">
                  <img src={resolveMedia(course.image)} alt={pick(lang, course, "title")} className="w-full h-full object-cover" />
                </div>
              ) : null}

              <span className="inline-block text-[11px] tracking-[0.2em] text-luxury-gold border border-luxury-border rounded-full px-4 py-1.5 mb-5">
                {pick(lang, course, "tag")}
              </span>
              <h1 data-testid="course-page-title" className="font-serif font-bold text-4xl sm:text-5xl lg:text-6xl text-luxury-bone leading-[1.25] mb-3">
                {pick(lang, course, "title")}
              </h1>
              <p className="text-luxury-gold text-lg mb-6">{pick(lang, course, "subtitle")}</p>
              <p className="text-luxury-gray text-base md:text-lg leading-loose whitespace-pre-line mb-8">{pick(lang, course, "description")}</p>

              <div className="flex flex-wrap items-center gap-3 mb-10">
                {(course.video_count || 0) > 0 && (
                  <span data-testid="course-page-lessons" className="flex items-center gap-2 text-sm text-luxury-bone border border-luxury-border rounded-full px-4 py-2">
                    <Play className="w-4 h-4 text-luxury-gold" strokeWidth={1.5} />
                    {course.video_count} {s.course_lessons}
                  </span>
                )}
                {(course.pdf_count || 0) > 0 && (
                  <span data-testid="course-page-pdfs" className="flex items-center gap-2 text-sm text-luxury-bone border border-luxury-border rounded-full px-4 py-2">
                    <FileText className="w-4 h-4 text-luxury-gold" strokeWidth={1.5} />
                    {course.pdf_count} {s.course_pdfs}
                  </span>
                )}
                <span className="flex items-center gap-2 text-sm text-luxury-gray border border-luxury-border rounded-full px-4 py-2">
                  <Gauge className="w-4 h-4 text-luxury-gold" strokeWidth={1.5} />
                  {pick(lang, course, "level")}
                </span>
              </div>

              <div className="flex flex-wrap items-center gap-5">
                <div>
                  {hasSpecial(course) && (
                    <p className="mb-3 flex items-center gap-3">
                      <span className="text-luxury-gray line-through">{course.price} {(course.currency || "usd").toUpperCase()}</span>
                      <span data-testid="course-page-special" className="text-[11px] tracking-wider bg-luxury-gold text-luxury-black font-bold px-3 py-1 rounded-full">
                        {ar ? "عرض خاص" : "SPECIAL OFFER"}
                      </span>
                    </p>
                  )}
                  <button
                    data-testid="course-page-buy"
                    onClick={buy}
                    className="inline-flex items-center justify-center gap-2 bg-luxury-gold text-luxury-black font-semibold px-10 py-4 rounded-full hover:bg-luxury-goldLight transition-colors duration-300 shadow-[0_10px_40px_rgba(212,175,55,0.25)]"
                  >
                    <Lock className="w-4 h-4" strokeWidth={1.5} />
                    {effPrice(course) > 0
                      ? ar
                        ? `اشترِ/اشترِي الآن — ${effPrice(course)} ${(course.currency || "usd").toUpperCase()}`
                        : `Buy now — ${effPrice(course)} ${(course.currency || "usd").toUpperCase()}`
                      : s.courses_enroll}
                  </button>
                </div>
                <a
                  href={waLink(settings, s.enrollMsg(pick(lang, course, "title")), lang)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 border border-luxury-border text-luxury-bone px-8 py-4 rounded-full hover:border-luxury-gold hover:text-luxury-gold transition-colors duration-300 text-sm"
                >
                  {ar ? "استفسار عبر واتساب" : "Ask on WhatsApp"}
                </a>
              </div>
            </motion.div>
          )}
        </div>
      </main>
      <Footer />
      <WhatsAppFloat />
    </div>
  );
}
